<script setup lang="ts">
import {
  cva,
  cx,
  ScalarButton,
  ScalarIcon,
  ScalarToggleInput,
} from '@scalar/components'
import {
  presets,
  themeLabels,
  type IntegrationThemeId,
  type Theme,
  type ThemeId,
} from '@scalar/themes'
import { computed } from 'vue'

import IntegrationLogo from '@/components/ImportCollection/IntegrationLogo.vue'

import Appearance from './components/Appearance.vue'
import Section from './components/Section.vue'
import { getThemeColors } from './helpers/get-theme-colors'

type ColorMode = 'system' | 'light' | 'dark'

const {
  activeProxyUrl,
  customThemes = [],
  activeThemeSlug,
  colorMode,
  telemetry,
} = defineProps<{
  /** Currently active proxy URL, when set to null means no proxy */
  activeProxyUrl?: string | null
  /** Custom themes available to the team */
  customThemes?: Theme[]
  /** Currently active theme ID */
  activeThemeSlug?: string
  /** Currently active color mode */
  colorMode: ColorMode
  /** Whether telemetry is enabled */
  telemetry?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:proxyUrl', value: string | null): void
  (e: 'update:themeSlug', value: string | undefined): void
  (e: 'update:colorMode', value: ColorMode): void
  (e: 'update:telemetry', value: boolean): void
}>()

const DEFAULT_PROXY_URL = 'https://proxy.scalar.com'

const THEME_IDS: Exclude<ThemeId, IntegrationThemeId>[] = [
  'none',
  'default',
  'alternate',
  'purple',
  'solarized',
  'saturn',
  'kepler',
]

const INTEGRATION_THEME_IDS: IntegrationThemeId[] = ['elysiajs', 'fastify']

const defaultThemes = THEME_IDS.map((themeId) => {
  if (themeId === 'none') {
    return {
      slug: themeId,
      name: 'None',
      description: 'No theme',
      theme: '',
    }
  }
  return presets[themeId]
})

const integrationThemes = INTEGRATION_THEME_IDS.map((themeId) => ({
  slug: themeId,
  name: themeLabels[themeId],
  description: themeLabels[themeId],
  theme: '',
}))

const customThemeSlugs = computed(
  () => new Set(customThemes.map((theme) => theme.slug)),
)

const buttonStyles = cva({
  base: 'w-full shadow-none text-c-1 justify-start pl-2 gap-2 border',
  variants: {
    active: {
      true: 'bg-primary text-c-1 hover:bg-inherit',
      false: 'bg-b-1 hover:bg-b-2',
    },
  },
})

/**
 * Determines if the "None" theme should be selected.
 * This happens when no theme is selected, or when the selected theme does not exist.
 */
const isNoneThemeSelected = computed(() => {
  if (activeThemeSlug === undefined || activeThemeSlug === 'none') {
    return true
  }

  const isValidTheme =
    customThemeSlugs.value.has(activeThemeSlug) ||
    defaultThemes.some((theme) => theme.slug === activeThemeSlug) ||
    integrationThemes.some((theme) => theme.slug === activeThemeSlug)

  return !isValidTheme
})

const isNoneTheme = (themeId: string): boolean => themeId === 'none'

const isThemeActive = (themeSlug: string): boolean => {
  return (
    activeThemeSlug === themeSlug ||
    (isNoneTheme(themeSlug) && isNoneThemeSelected.value)
  )
}

const checkmarkClasses = (isActive: boolean) =>
  cx(
    'flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] p-1',
    isActive && 'bg-c-accent text-b-1 border-transparent',
  )
</script>
<template>
  <div class="flex flex-col gap-10">
    <!-- Proxy -->
    <Section>
      <template #title>CORS Proxy</template>
      <template #description>
        Browsers block cross-origin requests for security. We provide a public
        proxy to
        <a
          class="hover:text-c-1 underline underline-offset-2"
          href="https://en.wikipedia.org/wiki/Cross-origin_resource_sharing"
          target="_blank"
          >bypass CORS issues</a
        >
        . Check the
        <a
          class="hover:text-c-1 underline underline-offset-2"
          href="https://github.com/scalar/scalar/tree/main/projects/proxy-scalar-com"
          target="_blank"
          >source code on GitHub</a
        >
        .
      </template>

      <div class="flex flex-col gap-2">
        <ScalarButton
          :class="
            cx(
              buttonStyles({
                active: activeProxyUrl === DEFAULT_PROXY_URL,
              }),
            )
          "
          @click="emit('update:proxyUrl', DEFAULT_PROXY_URL)">
          <div :class="checkmarkClasses(activeProxyUrl === DEFAULT_PROXY_URL)">
            <ScalarIcon
              v-if="activeProxyUrl === DEFAULT_PROXY_URL"
              icon="Checkmark"
              size="xs"
              thickness="3.5" />
          </div>
          Use proxy.scalar.com (default)
        </ScalarButton>

        <ScalarButton
          :class="cx(buttonStyles({ active: !activeProxyUrl }))"
          @click="emit('update:proxyUrl', null)">
          <div :class="checkmarkClasses(!activeProxyUrl)">
            <ScalarIcon
              v-if="!activeProxyUrl"
              icon="Checkmark"
              size="xs"
              thickness="3.5" />
          </div>
          Skip the proxy
        </ScalarButton>
      </div>
    </Section>

    <!-- Themes -->
    <Section>
      <template #title>Themes</template>
      <template #description>
        We've got a whole rainbow of themes for you to play with:
      </template>

      <div class="grid grid-cols-2 gap-2">
        <ScalarButton
          v-for="theme in defaultThemes"
          :key="theme.slug"
          :class="cx(buttonStyles({ active: isThemeActive(theme.slug) }))"
          @click="
            emit(
              'update:themeSlug',
              isNoneTheme(theme.slug) ? undefined : theme.slug,
            )
          ">
          <div class="flex items-center gap-2">
            <div :class="checkmarkClasses(isThemeActive(theme.slug))">
              <ScalarIcon
                v-if="isThemeActive(theme.slug)"
                icon="Checkmark"
                size="xs"
                thickness="3.5" />
            </div>
            {{ theme.name }}
          </div>
          <div class="flex items-center gap-1">
            <span
              class="border-c-3 -mr-3 inline-block h-5 w-5 rounded-full"
              :style="{
                backgroundColor: getThemeColors(theme.slug).light,
              }" />
            <span
              class="border-c-3 -mr-3 inline-block h-5 w-5 rounded-full"
              :style="{
                backgroundColor: getThemeColors(theme.slug).dark,
              }" />
            <span
              class="border-c-3 inline-block h-5 w-5 rounded-full"
              :style="{
                backgroundColor: getThemeColors(theme.slug).accent,
              }" />
          </div>
        </ScalarButton>
      </div>
    </Section>

    <!-- Frameworks -->
    <Section>
      <template #title>Framework Themes</template>
      <template #description>
        Are you a real fan? Show your support by using your favorite framework's
        theme!
      </template>

      <div class="grid grid-cols-2 gap-2">
        <ScalarButton
          v-for="theme in integrationThemes"
          :key="theme.slug"
          :class="cx(buttonStyles({ active: activeThemeSlug === theme.slug }))"
          @click="emit('update:themeSlug', theme.slug)">
          <div class="flex items-center gap-2">
            <div :class="checkmarkClasses(activeThemeSlug === theme.slug)">
              <ScalarIcon
                v-if="activeThemeSlug === theme.slug"
                icon="Checkmark"
                size="xs"
                thickness="3.5" />
            </div>
            {{ theme.name }}
          </div>
          <div class="flex items-center gap-1">
            <div class="size-7 rounded-xl">
              <IntegrationLogo :integration="theme.slug" />
            </div>
          </div>
        </ScalarButton>
      </div>
    </Section>

    <!-- Custom Themes -->
    <Section v-if="customThemes.length > 0">
      <template #title> Custom Themes </template>
      <template #description>
        Team defined themes are available to all workspaces in the team.
      </template>

      <div class="grid grid-cols-2 gap-2">
        <ScalarButton
          v-for="theme in customThemes"
          :key="theme.slug"
          :class="cx(buttonStyles({ active: activeThemeSlug === theme.slug }))"
          @click="emit('update:themeSlug', theme.slug)">
          <div class="flex items-center gap-2">
            <div :class="checkmarkClasses(activeThemeSlug === theme.slug)">
              <ScalarIcon
                v-if="activeThemeSlug === theme.slug"
                icon="Checkmark"
                size="xs"
                thickness="3.5" />
            </div>
            {{ theme.name }}
          </div>
        </ScalarButton>
      </div>
    </Section>

    <!-- Appearance -->
    <Section>
      <template #title>Appearance</template>
      <template #description>
        Choose between light, dark, or system-based appearance for your
        workspace.
      </template>

      <Appearance
        :colorMode="colorMode"
        @update:colorMode="(value) => emit('update:colorMode', value)" />
    </Section>

    <!-- Telemetry -->
    <Section>
      <template #title>Telemetry</template>
      <template #description>
        Help us improve Scalar by sending usage data.
      </template>

      <ScalarToggleInput
        :modelValue="telemetry"
        @update:modelValue="
          (value) => emit('update:telemetry', value ?? false)
        ">
        Enable telemetry
      </ScalarToggleInput>
    </Section>
  </div>
</template>
